// Field accessors shared by the Leads table and detail modal.
//
// A unified lead is still a CRM Lead, so the CRM organization/opportunity stays
// authoritative. These helpers only fill a gap from the attached Prospecting
// record - which is the only information a prospecting-only lead has.

import { cleanCell, isPlaceholder } from '../../data/transform'
import type { Lead } from '../../types/crm'
import type { ProspectingRecord } from '../../types/prospecting'
import type { UnifiedLead, UnifiedLeadSource } from '../../types/unifiedLead'

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i

// Sheet values that exist but say "there is nothing here".
const UNUSABLE_VALUES = new Set([
  'does not have a website',
  'no website',
  'not provided',
  'old calls',
])

function usable(value: unknown): string {
  const cleaned = cleanCell(value)
  if (!cleaned || isPlaceholder(cleaned)) return ''
  return UNUSABLE_VALUES.has(cleaned.toLowerCase().replace(/[.!]+$/, '')) ? '' : cleaned
}

/** The Prospecting record behind a unified lead, if this lead has one. */
export function getProspect(lead: Lead): ProspectingRecord | null {
  return (lead as Partial<UnifiedLead>).prospect ?? null
}

export function getLeadSource(lead: Lead): UnifiedLeadSource {
  return (lead as Partial<UnifiedLead>).source ?? 'crm'
}

/** True when the lead exists only in the Prospecting workbook. */
export function isProspectOnly(lead: Lead): boolean {
  return !lead.organization && !lead.opportunity && getProspect(lead) !== null
}

export function getLeadEmail(lead: Lead): string {
  const crmEmail = usable(lead.organization?.email)
  if (crmEmail) return crmEmail

  const prospectEmail = usable(getProspect(lead)?.email)
  return prospectEmail.match(EMAIL_PATTERN)?.[0] ?? ''
}

export function getLeadPhone(lead: Lead): string {
  return usable(lead.organization?.phone) || usable(getProspect(lead)?.phone)
}

export function getLeadWebsite(lead: Lead): string {
  return usable(lead.organization?.website) || usable(getProspect(lead)?.website)
}

/** The prospecting profile/social URL, which CRM leads carry as Facebook/LinkedIn. */
export function getLeadProfile(lead: Lead): string {
  return usable(getProspect(lead)?.profile)
}

/**
 * At least one real social profile: the Sheet's Facebook, Instagram or
 * LinkedIn column, or the Prospecting profile URL. CRM-only socials (TikTok,
 * YouTube, X) live per lead in Supabase and are not in the batch the Leads
 * page loads, so they do not count here.
 */
export function hasSocialProfile(lead: Lead): boolean {
  const organization = lead.organization
  return Boolean(
    usable(organization?.facebook) || usable(organization?.instagram) || usable(organization?.linkedin) || getLeadProfile(lead),
  )
}

/** What the prospect needs, as captured on the Prospecting sheet. */
export function getLeadNeeds(lead: Lead): string {
  return usable(getProspect(lead)?.needs)
}
